import { useForm } from '@brenger/react';
import { push } from 'connected-react-router';
import * as React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useDispatch } from 'react-redux';
import '../../../assets/user/user.scss';
import {
  Button,
  Col,
  FlashMessage,
  Heading,
  InputPassword,
  InputText,
  Row,
  SectionWrapper,
} from '../../../brenger-shared-ui';
import { r } from '../../../routes';
import { urlParser } from '../../../utils/basics';
import { translate } from '../../../utils/localization';
import { runValidators } from '../../../utils/validation';
import SocialButtons from '../components/SocialButtons';
import { actions } from '../ducks';
import { useLogin } from '../hooks/useLogin';
import { PersonSchema } from '../schema';
import { UserProperties } from '../typings';

export const LoginPage: React.FC = () => {
  const dispatch = useDispatch();
  const { mutate, isPending, error } = useLogin();

  const form = useForm({
    initialState: {
      email: '',
      password: '',
    },
    validators: {
      email: val => runValidators(val, PersonSchema[UserProperties.EMAIL]),
      password: val => runValidators(val, PersonSchema[UserProperties.PASSWORD]),
    },
  });

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (form.hasErrors) return;
    mutate({
      email: form.data.email.value,
      password: form.data.password.value,
    });
  };

  return (
    <SectionWrapper extraClasses={'login-wrapper'} sectionStyle={'white'} centerContent={true}>
      <form onSubmit={onSubmit} noValidate>
        <Heading size={2}>
          <Translate id={'authentication.login.headline'} />
        </Heading>
        <p>
          <Translate id={'authentication.login.no_account'} />{' '}
          <span
            className={'cursor-pointer trigger underline text-primary'}
            onClick={() => dispatch(push(r.user.register({ trId: urlParser().get('transport_request') })))}
          >
            <Translate id={'authentication.register.button'} />
          </span>
        </p>
        <div className={'pb-[1em]'}>
          <SocialButtons socialAuth={service => dispatch(actions.socialAuthStart(service, true))} />
        </div>
        <Row>
          <Col xs={12} extraClasses={'pt-[1em] input-default--space'}>
            <InputText
              autoComplete="email"
              name="user.email"
              type="text"
              placeholder={translate('form.fields.user.email.label')}
              value={form.data.email.value}
              onChange={email => form.set({ email })}
              meta={{
                error: form.getError('email'),
                showError: form.data.email.isDirty,
              }}
            />
          </Col>
          <Col xs={12} sm={12}>
            <InputPassword
              autoComplete="current-password"
              name="user.password"
              placeholder={translate('form.fields.user.password.label')}
              value={form.data.password.value}
              onChange={password => form.set({ password })}
              meta={{
                error: form.getError('password'),
                showError: form.data.password.isDirty,
              }}
            />
          </Col>
          <Col xs={12} sm={12} extraClasses={'login-errors'}>
            {error && <FlashMessage type={'error'} message={error.message} />}
          </Col>
          <Col xs={12} sm={12}>
            <Button
              fullWidth={true}
              loading={isPending}
              disabled={isPending || !form.isDirty || form.hasErrors}
              buttonStyle={'secondary'}
              type="submit"
            >
              <Translate id={'authentication.login.button'} />
            </Button>
          </Col>
          <Col xs={12} extraClasses={'pt-[0.5em]'}>
            <p>
              <Translate id={'authentication.reset.lost_password'} />{' '}
              <span
                className={'cursor-pointer trigger underline text-primary'}
                onClick={() => dispatch(push(r.user.passwordReset()))}
              >
                <Translate id={'authentication.general.click_here'} />
              </span>
            </p>
          </Col>
        </Row>
      </form>
    </SectionWrapper>
  );
};
